import {
    useCheckEmailDuplicateMutation,
    useCheckPhoneDuplicateMutation,
    usePutProfileFieldV2ApplyMutation,
    usePutProfileV2ApplyMutation,
    usePostValidateAllV2ApplyMutation,
    useLazyGetProfileV2ApplyAfterCvParsingQuery,
} from '@core/app/api/applyJobV2Api'
import type {
    ApplyV2TempAddress,
    ApplyV2TempProfileProfileData,
    GenderType,
    PutProfileFieldDataV2,
    PutProfileFieldDataV2Body,
} from '@core/app/types/applyV2Type'
import ApplyV2AddressSelect from '@core/components/ApplyDialogV2/ApplyV2AddressSelect'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2DateSelectGroup from '@core/components/ApplyDialogV2/ApplyV2DateSelectGroup'
import ApplyV2InputPhone from '@core/components/ApplyDialogV2/ApplyV2InputPhone'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import { nationalityOptions } from '@core/components/ApplyDialogV2/ApplyV2ProfileStep/helpers/nationalityOptions'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import ApplyV2SelectSingle from '@core/components/ApplyDialogV2/ApplyV2SelectSingle'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import getAddressStringFromObject from '@core/utils/helpers/getAddressStringFromObject'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import { formatDate } from 'date-fns'
import { useFormik } from 'formik'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import './ApplyV2ProfileStep2.scss'
import { debounce } from 'lodash'

const baseClass = 'ApplyV2ProfileStep2'

// Глобальний Set для відстеження UID, для яких вже викликали refetch після CV парсингу
const refetchedAfterCvSuccess = new Set<string>()

interface FormikValues {
    firstName: string | null | undefined // string | required | length 2-50. Errors: 12, 3, 5
    lastName: string | null | undefined // string | required | length 2-50. Errors: 12, 3, 5
    email: string | null | undefined // string | required | email pattern. Errors: 13
    title: string | null | undefined
    gender: GenderType | null | undefined // int | ?required | 0,1,2,3,4. Errors: 2, 4
    address: ApplyV2TempAddress | null | undefined // object | ?required. Errors: 12
    mobilePhone: {
        prefix: string | null | undefined // string| required | prefix enum. Errors 2,3
        number: string | null | undefined // string | required | /^[0-9]{5,12}+$/. Errors 20
    } | null // object | ?required. Errors: 12
    linkedin: string | null | undefined // string | ?required | /^(www.|https?:\/\/|)[a-zA-Z0-9]+\.[a-zA-Z0-9]+(.[a-zA-Z0-9]+)?/. Errors: 21, 3
    // nationality: 170
    nationality: number | string | null | undefined // int | ?required | nationality enum. Errors: 4
    // dateOfBirth: string | null | undefined // string | ?required | YYYY-MM-DD | age 14-95. Errors: 7, 15
    // dateOfBirth: string | null | undefined // string | ?required | YYYY-MM-DD | age 14-95. Errors: 7, 15
    dateOfBirth:
        | {
              day: number | string | null | undefined
              month: number | string | null | undefined
              year: number | string | null | undefined
          }
        | null
        | undefined

    // startDateDay:  string | null | undefined,
    // startDateMonth:  string | null | undefined,
    // startDateYear:  string | null | undefined,
}

type FormikFieldKey = keyof FormikValues

function ApplyV2ProfileStep2() {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const {
        postProfileData,
        onNextStep,
        postProfileRefetch,
        isPostProfileLoading,
        isPostApplied,
        settingsData,
        candidateId,
        storageData,
        incrementProfileVisitCount,
        postValidateAll,
        cvParserStatus,
        setIsRefetchingAfterCvParsing,
    } = useApplyV2Context()
    const [putProfile, { isLoading, isSuccess, isError }] = usePutProfileV2ApplyMutation()
    const [putProfileField, { isLoading: isFieldLoading }] = usePutProfileFieldV2ApplyMutation()
    const [checkEmailDuplicate, { isLoading: isCheckEmailDuplicateLoading }] = useCheckEmailDuplicateMutation()
    const [checkPhoneDuplicate, { isLoading: isCheckPhoneDuplicateLoading }] = useCheckPhoneDuplicateMutation()
    const debounceTimers = useRef<Partial<Record<FormikFieldKey, any>>>({})
    const [isEmailDuplicate, setIsEmailDuplicate] = useState(false)
    const [isPhoneDuplicate, setIsPhoneDuplicate] = useState(false)
    const [loadingFields, setLoadingFields] = useState<Set<keyof FormikValues>>(new Set())
    const [isFormInitialized, setIsFormInitialized] = useState(false)
    const hasValidatedRef = useRef(false)
    const lastValidatedUidRef = useRef<string | null>(null)
    const hasUserInteractedRef = useRef(false)
    const isRefetchingAfterCvParsingRef = useRef(false)
    const formRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        return () => {
            Object.values(debounceTimers.current).forEach(clearTimeout)
        }
    }, [])

    useEffect(() => {
        if (
            postProfileData?.data?.profile?.mobilePhone?.number &&
            postProfileData?.data?.profile?.mobilePhone?.number &&
            postProfileData?.data?.uid
        ) {
            putProfileField({
                uid: postProfileData.data.uid,
                body: {
                    mobilePhone: {
                        number: postProfileData.data.profile.mobilePhone.number,
                        prefix: postProfileData.data.profile.mobilePhone.prefix,
                    },
                },
            })
                .unwrap()
                .then((result) => {})
                .catch((error: any) => {
                    if (error?.data?.errors && Array.isArray(error.data.errors)) {
                        const formikErrors: any = { ...formik.errors }
                        error.data.errors.forEach((err: { msg: string; field: string; code: number }) => {
                            switch (err.field) {
                                case 'mobilePhone':
                                    if (err.code === 1206) {
                                        setIsPhoneDuplicate(true)
                                    }
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: err.msg, number: err.msg }
                                    } else {
                                        formikErrors.mobilePhone.prefix = err.msg
                                        formikErrors.mobilePhone.number = err.msg
                                    }
                                    break
                                case 'email':
                                    if (err.code === 1205) {
                                        setIsEmailDuplicate(true)
                                    }
                                    formikErrors[err.field] = err.msg
                                    break
                                default:
                                    formikErrors[err.field] = err.msg
                                    break
                            }
                        })
                        formik.setErrors(formikErrors)
                    }
                })
        }
    }, [
        postProfileData?.data?.profile?.mobilePhone?.number,
        postProfileData?.data?.profile?.mobilePhone?.number,
        postProfileData?.data?.uid,
    ])

    useEffect(() => {
        if (
            postProfileData?.data?.profile?.mobilePhone?.number &&
            postProfileData?.data?.profile?.mobilePhone?.number &&
            postProfileData?.data?.uid
        ) {
            putProfileField({
                uid: postProfileData.data.uid,
                body: {
                    mobilePhone: {
                        number: postProfileData.data.profile.mobilePhone.number,
                        prefix: postProfileData.data.profile.mobilePhone.prefix,
                    },
                },
            })
                .unwrap()
                .then((result) => {})
                .catch((error: any) => {
                    if (error?.data?.errors && Array.isArray(error.data.errors)) {
                        const formikErrors: any = { ...formik.errors }
                        error.data.errors.forEach((err: { msg: string; field: string; code: number }) => {
                            switch (err.field) {
                                case 'mobilePhone':
                                    if (err.code === 1206) {
                                        setIsPhoneDuplicate(true)
                                    }
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: err.msg, number: err.msg }
                                    } else {
                                        formikErrors.mobilePhone.prefix = err.msg
                                        formikErrors.mobilePhone.number = err.msg
                                    }
                                    break
                                case 'email':
                                    if (err.code === 1205) {
                                        setIsEmailDuplicate(true)
                                    }
                                    formikErrors[err.field] = err.msg
                                    break
                                default:
                                    formikErrors[err.field] = err.msg
                                    break
                            }
                        })
                        formik.setErrors(formikErrors)
                    }
                })
        }
    }, [
        postProfileData?.data?.profile?.mobilePhone?.number,
        postProfileData?.data?.profile?.mobilePhone?.number,
        postProfileData?.data?.uid,
    ])

    useEffect(() => {
        if (postProfileData?.data?.profile?.email && postProfileData?.data?.uid) {
            putProfileField({ uid: postProfileData.data.uid, body: { email: postProfileData.data.profile.email } })
                .unwrap()
                .then((result) => {})
                .catch((error: any) => {
                    if (error?.data?.errors && Array.isArray(error.data.errors)) {
                        const formikErrors: any = { ...formik.errors }
                        error.data.errors.forEach((err: { msg: string; field: string; code: number }) => {
                            switch (err.field) {
                                case 'mobilePhone':
                                    if (err.code === 1206) {
                                        setIsPhoneDuplicate(true)
                                    }
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: err.msg, number: err.msg }
                                    } else {
                                        formikErrors.mobilePhone.prefix = err.msg
                                        formikErrors.mobilePhone.number = err.msg
                                    }
                                    break
                                case 'email':
                                    if (err.code === 1205) {
                                        setIsEmailDuplicate(true)
                                    }
                                    formikErrors[err.field] = err.msg
                                    break
                                default:
                                    formikErrors[err.field] = err.msg
                                    break
                            }
                        })
                        formik.setErrors(formikErrors)
                    }
                })
        }
    }, [postProfileData?.data?.profile?.email, postProfileData?.data?.uid])

    useEffect(() => {
        if (postProfileData?.data?.profile?.email && postProfileData?.data?.uid) {
            putProfileField({ uid: postProfileData.data.uid, body: { email: postProfileData.data.profile.email } })
                .unwrap()
                .then((result) => {})
                .catch((error: any) => {
                    if (error?.data?.errors && Array.isArray(error.data.errors)) {
                        const formikErrors: any = { ...formik.errors }
                        error.data.errors.forEach((err: { msg: string; field: string; code: number }) => {
                            switch (err.field) {
                                case 'mobilePhone':
                                    if (err.code === 1206) {
                                        setIsPhoneDuplicate(true)
                                    }
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: err.msg, number: err.msg }
                                    } else {
                                        formikErrors.mobilePhone.prefix = err.msg
                                        formikErrors.mobilePhone.number = err.msg
                                    }
                                    break
                                case 'email':
                                    if (err.code === 1205) {
                                        setIsEmailDuplicate(true)
                                    }
                                    formikErrors[err.field] = err.msg
                                    break
                                default:
                                    formikErrors[err.field] = err.msg
                                    break
                            }
                        })
                        formik.setErrors(formikErrors)
                    }
                })
        }
    }, [postProfileData?.data?.profile?.email, postProfileData?.data?.uid])

    const addLoadingField = (field: keyof FormikValues) => {
        setLoadingFields((prev) => new Set(prev).add(field))
    }

    const removeLoadingField = (field: keyof FormikValues) => {
        setLoadingFields((prev) => {
            const newSet = new Set(prev)
            newSet.delete(field)
            return newSet
        })
    }

    const isLoadingField = (field: keyof FormikValues) => loadingFields.has(field)

    const profile = postProfileData?.data?.profile

    const fields = settingsData?.steps?.profile?.fields

    const orderedList = Object.values(fields ?? {})
        .sort((a, b) => a.order - b.order)
        .filter((it) => it.visible)

    // console.log(orderedList)

    const isFirstNameVisible = fields?.firstName.visible
    const isFirstNameRequired = fields?.firstName.required

    const isLastNameVisible = fields?.lastName.visible
    const isLastNameRequired = fields?.lastName.required

    const isEmailVisible = fields?.email.visible
    const isEmailRequired = fields?.email.required

    const isLinkedInVisible = fields?.linkedin.visible
    const isLinkedInRequired = fields?.linkedin.required

    const isTitleVisible = fields?.title.visible
    const isTitleRequired = fields?.title.required

    const isGenderVisible = fields?.gender.visible
    const isGenderRequired = fields?.gender.required

    const isNationalityVisible = fields?.nationality.visible
    const isNationalityRequired = fields?.nationality.required

    const isDateOfBirthVisible = fields?.dateOfBirth.visible
    const isDateOfBirthRequired = fields?.dateOfBirth.required

    const isMobilePhoneVisible = fields?.mobilePhone.visible
    const isMobilePhoneRequired = fields?.mobilePhone.required

    const isAddressVisible = fields?.address.visible
    const isAddressRequired = fields?.address.required

    const validationSchema = Yup.object().shape({
        firstName:
            isFirstNameVisible && isFirstNameRequired
                ? Yup.string()
                      .trim()
                      .required(getTitle('5653', 'Required'))
                      .min(
                          2,
                          `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`
                      )
                      .max(
                          50,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 50 })}`
                      )
                : Yup.string()
                      .trim()
                      .notRequired()
                      .nullable()
                      .min(
                          2,
                          `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`
                      )
                      .max(
                          50,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 50 })}`
                      ),
        lastName:
            isLastNameVisible && isLastNameRequired
                ? Yup.string()
                      .trim()
                      .required(getTitle('5653', 'Required'))
                      .min(
                          2,
                          `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`
                      )
                      .max(
                          50,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 50 })}`
                      )
                : Yup.string()
                      .trim()
                      .notRequired()
                      .nullable()
                      .min(
                          2,
                          `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`
                      )
                      .max(
                          50,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 50 })}`
                      ),
        email:
            isEmailVisible && isEmailRequired
                ? Yup.string()
                      .trim()
                      .required(getTitle('5653', 'Required'))
                      .email(getTitle('6598', 'Email is invalid'))
                      .max(
                          255,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`
                      )
                : Yup.string()
                      .trim()
                      .notRequired()
                      .nullable()
                      .email(getTitle('6598', 'Email is invalid'))
                      .max(
                          255,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`
                      ),

        title:
            isTitleVisible && isTitleRequired
                ? Yup.string()
                      .trim()
                      .required(getTitle('5653', 'Required'))
                      .min(
                          2,
                          `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`
                      )
                      .max(
                          128,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 128 })}`
                      )
                : Yup.string()
                      .trim()
                      .min(
                          2,
                          `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`
                      )
                      .max(
                          128,
                          `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 128 })}`
                      )
                      .notRequired()
                      .nullable(),
        gender:
            isGenderVisible && isGenderRequired
                ? Yup.mixed<GenderType>()
                      .required(getTitle('5653', 'Required'))
                      .oneOf([1, 2, 3, 4, 5], getTitle('5653', 'Required'))
                : Yup.mixed<GenderType | null | undefined>()
                      .oneOf([0, 1, 2, 3, 4, null, undefined], getTitle('7943', 'Gender is invalid'))
                      .notRequired()
                      .nullable(),
        linkedin:
            isLinkedInVisible && isLinkedInRequired
                ? Yup.string()
                      .trim()
                      .required(getTitle('5653', 'Required'))
                      // .url(getTitle('9798', 'LinkedIn link is invalid'))
                      .matches(/^(https?:\/\/)?(www\.)?linkedin\.com\/.+/, getTitle('9798', 'LinkedIn link is invalid'))
                : Yup.string()
                      .trim()
                      // .url(getTitle('9798', 'LinkedIn link is invalid'))
                      .matches(/^(https?:\/\/)?(www\.)?linkedin\.com\/.+/, getTitle('9798', 'LinkedIn link is invalid'))
                      .notRequired()
                      .nullable(),
        nationality:
            isNationalityVisible && isNationalityRequired
                ? Yup.number()
                      .typeError(getTitle('907', 'Invalid'))
                      .required(getTitle('5653', 'Required'))
                      .moreThan(0, getTitle('5653', 'Required'))
                : Yup.number().nullable().notRequired(),
        address:
            isAddressVisible && isAddressRequired
                ? Yup.object()
                      .shape({
                          // country: Yup.string().required(getTitle('5653', 'Required')),
                          country: Yup.string().notRequired().nullable(),
                          city: Yup.string().notRequired().nullable(),
                          postalCode: Yup.string().notRequired().nullable(),
                          address1: Yup.string().notRequired().nullable(),
                          address2: Yup.string().notRequired().nullable(),
                          label: Yup.string().notRequired().nullable(),
                      })
                      .nullable()
                      .test(
                          'address-required',
                          getTitle('5653', 'Required'),
                          function (value: { label: string; country: string } | null) {
                              // console.log(value, this)
                              if (!value || !value.label || !value.country) {
                                  return false
                              }
                              return true
                          }
                      )
                : Yup.object()
                      .shape({
                          country: Yup.string().notRequired().nullable(),
                          city: Yup.string().notRequired().nullable(),
                          postalCode: Yup.string().notRequired().nullable(),
                          address1: Yup.string().notRequired().nullable(),
                          address2: Yup.string().notRequired().nullable(),
                          label: Yup.string().notRequired().nullable(),
                      })
                      .nullable(),
        mobilePhone:
            isMobilePhoneVisible && isMobilePhoneRequired
                ? Yup.object()
                      .shape({
                          prefix: Yup.string().trim().required(getTitle('10473', 'Phone prefix is required')),
                          number: Yup.string()
                              .trim()
                              .matches(/^[0-9]{5,12}$/, getTitle('4724', 'Your phone number is invalid'))
                              .required(getTitle('10473', 'Phone prefix is required')),
                      })
                      .required(getTitle('5653', 'Required'))
                : Yup.object()
                      .shape({
                          prefix: Yup.string().notRequired().nullable().trim(),
                          number: Yup.string()
                              .trim()
                              .matches(/^[0-9]{5,12}$/, getTitle('4724', 'Your phone number is invalid'))
                              .notRequired()
                              .nullable(),
                      })
                      .test('phone-validation', getTitle('4724', 'Your phone number is invalid'), function (value) {
                          if (!value?.prefix && !value?.number) {
                              return true
                          }

                          const isPrefixValid = value?.prefix && value.prefix.trim().length > 0
                          const isNumberValid = value?.number && /^[0-9]{5,12}$/.test(value.number.trim())

                          if (isNumberValid && !isPrefixValid) {
                              return this.createError({
                                  message: getTitle('5699', 'Invalid prefix'),
                              })
                          }

                          return !!isPrefixValid && !!isNumberValid
                      })
                      .notRequired()
                      .nullable(),
        dateOfBirth:
            isDateOfBirthVisible && isDateOfBirthRequired
                ? Yup.object()
                      .shape({
                          day: Yup.string().trim().required(getTitle('5653', 'Required')),
                          month: Yup.string().trim().required(getTitle('5653', 'Required')),
                          year: Yup.string().trim().required(getTitle('5653', 'Required')),
                      })
                      .required(getTitle('5653', 'Required'))
                      .test(
                          'age-validation',
                          getTitle('age_validation', getTitle('10578', 'You must be at least 18 years old')),
                          function (value: any, context: any) {
                              const { path, createError } = context
                              if (!value?.day || !value?.month || !value?.year) {
                                  return true
                              }

                              const birthDate = new Date(Number(value.year), Number(value.month) - 1, Number(value.day))

                              const isValid =
                                  birthDate.getFullYear() === Number(value.year) &&
                                  birthDate.getMonth() === Number(value.month) - 1 &&
                                  birthDate.getDate() === Number(value.day)

                              if (!isValid) {
                                  return createError({ path: `${path}.day`, message: getTitle('148', 'Invalid date') })
                              }

                              if (isNaN(birthDate.getTime())) {
                                  console.log('birth_date', birthDate)
                                  return createError({ path: `${path}.day`, message: getTitle('148', 'Invalid date') })
                              }

                              const today = new Date()
                              let age = today.getFullYear() - birthDate.getFullYear()

                              const monthDiff = today.getMonth() - birthDate.getMonth()
                              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                                  age--
                              }

                              if (age < 18) {
                                  return createError({
                                      path: `${path}.year`,
                                      message: getTitle('10578', 'You must be at least 18 years old'),
                                  })
                              }

                              return true
                          }
                      )
                : Yup.object()
                      .shape({
                          day: Yup.string().notRequired().nullable(),
                          month: Yup.string().notRequired().nullable(),
                          year: Yup.string().notRequired().nullable(),
                      })
                      .notRequired()
                      .nullable()
                      .test(
                          'age-validation',
                          getTitle('age_validation', getTitle('10578', 'You must be at least 18 years old')),
                          function (value: any, context: any) {
                              const { path, createError } = context
                              if (!value?.day || !value?.month || !value?.year) {
                                  return true
                              }

                              const birthDate = new Date(Number(value.year), Number(value.month) - 1, Number(value.day))

                              const isValid =
                                  birthDate.getFullYear() === Number(value.year) &&
                                  birthDate.getMonth() === Number(value.month) - 1 &&
                                  birthDate.getDate() === Number(value.day)

                              if (!isValid) {
                                  return createError({ path: `${path}.day`, message: getTitle('148', 'Invalid date') })
                              }

                              if (isNaN(birthDate.getTime())) {
                                  console.log('birth_date', birthDate)
                                  return createError({ path: `${path}.day`, message: getTitle('148', 'Invalid date') })
                              }

                              const today = new Date()
                              let age = today.getFullYear() - birthDate.getFullYear()

                              const monthDiff = today.getMonth() - birthDate.getMonth()
                              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                                  age--
                              }

                              if (age < 18) {
                                  return createError({
                                      path: `${path}.year`,
                                      message: getTitle('10578', 'You must be at least 18 years old'),
                                  })
                              }

                              return true
                          }
                      ),
    })

    const dob = profile?.dateOfBirth?.split('-') ?? []

    const formik = useFormik<FormikValues>({
        initialValues: {
            firstName: profile?.firstName ?? '',
            lastName: profile?.lastName ?? '',
            title: isTitleVisible ? profile?.title : null,
            email: isEmailVisible ? profile?.email : null,
            gender: isGenderVisible ? profile?.gender : null,
            linkedin: isLinkedInVisible ? profile?.linkedin : null,
            nationality: isNationalityVisible ? profile?.nationality : null,
            address: isAddressVisible
                ? profile?.address
                    ? {
                          label: getAddressStringFromObject(profile.address),
                          ...profile.address,
                      }
                    : {
                          country: '',
                          city: '',
                          postalCode: '',
                          address1: '',
                          address2: '',
                          label: '',
                      }
                : null,
            mobilePhone: isMobilePhoneVisible
                ? profile?.mobilePhone
                    ? {
                          prefix:
                              profile.mobilePhone.prefix && !profile.mobilePhone.prefix.startsWith('+')
                                  ? `+${profile.mobilePhone.prefix}`
                                  : profile.mobilePhone.prefix || '',
                          number: profile.mobilePhone.number || '',
                      }
                    : {
                          prefix: '',
                          number: '',
                      }
                : null,
            dateOfBirth: isDateOfBirthVisible
                ? {
                      day: dob[2] === '00' ? null : dob[2],
                      month: dob[1] === '00' ? null : dob[1],
                      year: dob[0] === '0000' ? null : dob[0],
                  }
                : null,
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            // console.log('SUBMIT: ', values, formik.touched, formik.errors)
            if (!postProfileData?.data?.uid) return
            try {
                const dob =
                    values.dateOfBirth?.year && values.dateOfBirth?.month && values.dateOfBirth?.day
                        ? `${values.dateOfBirth.year}-${values.dateOfBirth.month}-${values.dateOfBirth.day}`
                        : null
                const addr =
                    values.address?.country ||
                    values.address?.city ||
                    values.address?.postalCode ||
                    values.address?.address1 ||
                    values.address?.address2
                        ? {
                              country: values.address?.country ?? '',
                              city: values.address?.city ?? '',
                              postalCode: values.address?.postalCode ?? '',
                              address1: values.address?.address1 ?? '',
                              address2: values.address?.address2 ?? '',
                          }
                        : null
                const phone =
                    values.mobilePhone?.prefix && values.mobilePhone?.number
                        ? {
                              prefix: values.mobilePhone?.prefix ?? '',
                              number: values.mobilePhone?.number ?? '',
                          }
                        : null
                const body = {
                    firstName: values.firstName ? values.firstName.trim() : '',
                    lastName: values.lastName ? values.lastName.trim() : '',
                    email: values.email ? values.email.trim() : '',
                    linkedin: values.linkedin ? values.linkedin.trim() : null,
                    gender: values.gender ?? null,
                    dateOfBirth: dob !== '0000-00-00' ? dob : null,
                    address: addr,
                    mobilePhone: phone,
                    nationality: values.nationality ?? null,
                    title: values.title ? values.title.trim() : null,
                }

                const response = await putProfile({ uid: postProfileData.data.uid, body }).unwrap()
                onNextStep()
            } catch (error: any) {
                if (error?.data?.errors && Array.isArray(error.data.errors)) {
                    const formikErrors: any = { ...formik.errors }

                    error.data.errors.forEach((err: any) => {
                        switch (err.field) {
                            case 'mobilePhone':
                                if (!formikErrors.mobilePhone) {
                                    formikErrors.mobilePhone = { prefix: '', number: '' }
                                }
                                formikErrors.mobilePhone.prefix = err.msg
                                formikErrors.mobilePhone.number = err.msg
                                break
                            case 'mobilePhone.prefix':
                                if (!formikErrors.mobilePhone) {
                                    formikErrors.mobilePhone = { prefix: '', number: '' }
                                }
                                formikErrors.mobilePhone.prefix = err.msg
                                break
                            case 'mobilePhone.number':
                                if (!formikErrors.mobilePhone) {
                                    formikErrors.mobilePhone = { prefix: '', number: '' }
                                }
                                formikErrors.mobilePhone.number = err.msg
                                break
                            case 'dateOfBirth':
                                formikErrors.dateOfBirth = { day: err.msg, month: err.msg, year: err.msg }
                                break
                            case 'firstName':
                            case 'lastName':
                            case 'email':
                            case 'linkedin':
                            case 'gender':
                            case 'address':
                            case 'nationality':
                            case 'title':
                                formikErrors[err.field] = err.msg
                                break
                            default:
                                formikErrors[err.field] = err.msg
                                break
                        }
                    })
                    console.log(formikErrors)
                    setErrors(formikErrors)
                } else {
                    setErrors({
                        root: error.message,
                    } as any)
                }
            }
        },
        enableReinitialize: true,
        validateOnChange: true,
    })
    const scrollToFirstError = useCallback(() => {
        const errorFields = Object.keys(formik.errors)
        if (errorFields.length === 0) return

        const fieldOrder = orderedList.map((r) => r.name)

        const firstErrorField = fieldOrder.find((field) => errorFields.includes(field))
        if (!firstErrorField) return

        let errorElement: HTMLElement | null = null

        if (firstErrorField === 'mobilePhone') {
            errorElement = document.querySelector('[name="mobilePhone"]')
        } else if (firstErrorField === 'dateOfBirth') {
            errorElement = document.querySelector('[name="dateOfBirth.day"]')
        } else {
            errorElement = document.querySelector(`[name="${firstErrorField}"]`)
        }

        if (!errorElement) {
            errorElement = document.querySelector(`.${baseClass}__${firstErrorField}`)
        }

        if (errorElement) {
            errorElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })

            const inputElement = errorElement.querySelector('input, select, textarea') as HTMLElement
            if (inputElement) {
                inputElement.focus()
            }
        }
    }, [formik.errors])

    useEffect(() => {
        console.log('[ApplyV2ProfileStep2] isFormInitialized changed:', isFormInitialized)
    }, [isFormInitialized])

    const [
        fetchProfileAfterCvParsing,
        {
            isFetching: isFetchingAfterCvParsing,
            isLoading: isLoadingAfterCvParsing,
            data: dataAfterCvParsing,
            error: errorAfterCvParsing,
        },
    ] = useLazyGetProfileV2ApplyAfterCvParsingQuery()

    useEffect(() => {
        if (isFetchingAfterCvParsing && isRefetchingAfterCvParsingRef.current) {
            console.log('[ApplyV2ProfileStep2] ✅ Setting isRefetchingAfterCvParsing to TRUE (fetching started)')
            setIsRefetchingAfterCvParsing(true)
        }
    }, [isFetchingAfterCvParsing, setIsRefetchingAfterCvParsing])

    useEffect(() => {
        console.log('[ApplyV2ProfileStep2] Component mounted, incrementing profile visit count')
        incrementProfileVisitCount()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (cvParserStatus !== 'success' || storageData?.currentStep !== 'profile') {
            return
        }

        const uid = postProfileData?.data?.uid
        if (!uid) {
            console.log('[ApplyV2ProfileStep2] No UID available, skipping refetch')
            return
        }

        if (refetchedAfterCvSuccess.has(uid)) {
            console.log('[ApplyV2ProfileStep2] Already refetched profile for this UID after CV success, skipping...', {
                uid,
            })
            return
        }

        refetchedAfterCvSuccess.add(uid)
        isRefetchingAfterCvParsingRef.current = true

        setIsRefetchingAfterCvParsing(true)

        const fetchPromise = fetchProfileAfterCvParsing({ uid })

        fetchPromise
            .unwrap()
            .then((result) => {
                console.log(
                    '[ApplyV2ProfileStep2] ✅ Profile data fetched after CV parsing successfully (SEPARATE QUERY)',
                    {
                        result,
                        timestamp: new Date().toISOString(),
                        currentState: {
                            isFetchingAfterCvParsing,
                            isLoadingAfterCvParsing,
                        },
                    }
                )

                const newProfile = result?.data?.profile
                if (newProfile) {
                    const dob = newProfile.dateOfBirth?.split('-') ?? []

                    const newFormValues: Partial<FormikValues> = {
                        firstName: newProfile.firstName ?? formik.values.firstName,
                        lastName: newProfile.lastName ?? formik.values.lastName,
                        title: isTitleVisible ? (newProfile.title ?? formik.values.title) : formik.values.title,
                        email: isEmailVisible ? (newProfile.email ?? formik.values.email) : formik.values.email,
                        gender: isGenderVisible ? (newProfile.gender ?? formik.values.gender) : formik.values.gender,
                        linkedin: isLinkedInVisible
                            ? (newProfile.linkedin ?? formik.values.linkedin)
                            : formik.values.linkedin,
                        nationality: isNationalityVisible
                            ? (newProfile.nationality ?? formik.values.nationality)
                            : formik.values.nationality,
                        address: isAddressVisible
                            ? newProfile.address
                                ? {
                                      label: getAddressStringFromObject(newProfile.address),
                                      ...newProfile.address,
                                  }
                                : formik.values.address
                            : formik.values.address,
                        mobilePhone: isMobilePhoneVisible
                            ? newProfile.mobilePhone
                                ? {
                                      prefix:
                                          newProfile.mobilePhone.prefix &&
                                          !newProfile.mobilePhone.prefix.startsWith('+')
                                              ? `+${newProfile.mobilePhone.prefix}`
                                              : newProfile.mobilePhone.prefix || '',
                                      number: newProfile.mobilePhone.number || '',
                                  }
                                : formik.values.mobilePhone
                            : formik.values.mobilePhone,
                        dateOfBirth: isDateOfBirthVisible
                            ? {
                                  day: dob[2] === '00' ? null : (dob[2] ?? formik.values.dateOfBirth?.day),
                                  month: dob[1] === '00' ? null : (dob[1] ?? formik.values.dateOfBirth?.month),
                                  year: dob[0] === '0000' ? null : (dob[0] ?? formik.values.dateOfBirth?.year),
                              }
                            : formik.values.dateOfBirth,
                    }

                    console.log('[ApplyV2ProfileStep2] 📝 Setting form values:', {
                        newFormValues,
                        timestamp: new Date().toISOString(),
                    })

                    formik.setValues({
                        ...formik.values,
                        ...newFormValues,
                    } as FormikValues)
                } else {
                    console.warn('[ApplyV2ProfileStep2] ⚠️ No profile data in result, skipping form update', {
                        result,
                        timestamp: new Date().toISOString(),
                    })
                }

                isRefetchingAfterCvParsingRef.current = false
                setIsRefetchingAfterCvParsing(false)
            })
            .catch((error) => {
                console.error('[ApplyV2ProfileStep2] ❌ Error fetching/refetching profile data after CV parsing:', {
                    error,
                    timestamp: new Date().toISOString(),
                    currentState: {
                        isFetchingAfterCvParsing,
                        isLoadingAfterCvParsing,
                        isPostProfileLoading,
                    },
                })
                refetchedAfterCvSuccess.delete(uid)
                isRefetchingAfterCvParsingRef.current = false
                setIsRefetchingAfterCvParsing(false)
            })
    }, [
        cvParserStatus,
        storageData?.currentStep,
        postProfileData?.data?.uid,
        fetchProfileAfterCvParsing,
        setIsRefetchingAfterCvParsing,
        formik,
        isTitleVisible,
        isEmailVisible,
        isGenderVisible,
        isLinkedInVisible,
        isNationalityVisible,
        isAddressVisible,
        isMobilePhoneVisible,
        isDateOfBirthVisible,
    ])

    useEffect(() => {
        console.log('[ApplyV2ProfileStep2] postProfileData changed:', postProfileData)
        console.log('[ApplyV2ProfileStep2] formik.values:', formik.values)
        console.log('[ApplyV2ProfileStep2] storageData:', storageData)

        if (!profile) {
            console.log('[ApplyV2ProfileStep2] No profile in postProfileData, skipping form initialization check')
            setIsFormInitialized(false)
            hasValidatedRef.current = false
            return
        }

        const isFormNotEmpty = Boolean(
            formik.values.firstName?.trim() ||
                formik.values.lastName?.trim() ||
                formik.values.email?.trim() ||
                formik.values.linkedin?.trim() ||
                formik.values.mobilePhone?.prefix?.trim() ||
                formik.values.mobilePhone?.number?.trim() ||
                formik.values.gender ||
                formik.values.nationality ||
                formik.values.title?.trim() ||
                formik.values.dateOfBirth?.day ||
                formik.values.dateOfBirth?.month ||
                formik.values.dateOfBirth?.year ||
                formik.values.address
        )

        const profileVisitCount = storageData?.profileVisitCount || 0
        const isFirstVisit = profileVisitCount <= 1

        console.log('[ApplyV2ProfileStep2] Form initialized:', {
            isFormNotEmpty,
            formValues: formik.values,
            hasProfile: !!profile,
            isFirstVisit,
            profileVisitCount,
            shouldShowValidationErrors: isFormNotEmpty && !isFirstVisit,
        })

        setIsFormInitialized(isFormNotEmpty)

        if (!isFormNotEmpty || isFirstVisit) {
            hasValidatedRef.current = false
        }
    }, [postProfileData?.data?.profile, formik.values, profile])

    useEffect(() => {
        console.log('[ApplyV2ProfileStep2] Validation useEffect triggered:', {
            isFormInitialized,
            uid: postProfileData?.data?.uid,
            hasValidated: hasValidatedRef.current,
        })

        const currentUid = postProfileData?.data?.uid
        const profileVisitCount = storageData?.profileVisitCount || 0
        const isFirstVisit = profileVisitCount <= 1

        if (currentUid && lastValidatedUidRef.current !== currentUid) {
            hasValidatedRef.current = false
            lastValidatedUidRef.current = currentUid
        }

        if (!isFormInitialized || !currentUid || hasValidatedRef.current || isFirstVisit) {
            console.log('[ApplyV2ProfileStep2] Validation skipped:', {
                isFormInitialized,
                hasUid: !!currentUid,
                hasValidated: hasValidatedRef.current,
                isFirstVisit,
                profileVisitCount,
                lastValidatedUid: lastValidatedUidRef.current,
            })
            return
        }

        let isMounted = true

        const validateProfile = async () => {
            hasValidatedRef.current = true
            lastValidatedUidRef.current = currentUid
            console.log('[ApplyV2ProfileStep2] Starting validation...')
            try {
                const uid = postProfileData?.data?.uid
                if (!uid) {
                    console.log('[ApplyV2ProfileStep2] No UID, skipping validation')
                    return
                }
                console.log('[ApplyV2ProfileStep2] Calling validateAll with uid:', uid)
                const result = await postValidateAll()

                console.log('[ApplyV2ProfileStep2] Validation response:', result)
                console.log('[ApplyV2ProfileStep2] Validation response statusCode:', result.statusCode)
                console.log('[ApplyV2ProfileStep2] Validation response success:', result.success)
                console.log('[ApplyV2ProfileStep2] Validation response errors:', result.errors)

                if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
                    console.log('[ApplyV2ProfileStep2] Found errors in response:', result.errors)
                    const firstError = result.errors[0]
                    console.log('[ApplyV2ProfileStep2] First error:', firstError)
                    if (firstError.profileErrors && Array.isArray(firstError.profileErrors)) {
                        console.log('[ApplyV2ProfileStep2] Found profileErrors:', firstError.profileErrors)
                        const formikErrors: any = {}

                        for (const profileError of firstError.profileErrors) {
                            const { field, msg } = profileError
                            console.log('[ApplyV2ProfileStep2] Processing profileError:', { field, msg })

                            switch (field) {
                                case 'firstName':
                                case 'lastName':
                                case 'email':
                                case 'linkedin':
                                case 'gender':
                                case 'address':
                                case 'nationality':
                                case 'title':
                                    formikErrors[field] = msg || 'Required'
                                    break
                                case 'mobilePhone':
                                case 'mobilePhone.number':
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: '', number: '' }
                                    }
                                    formikErrors.mobilePhone.number = msg || 'Invalid phone'
                                    break
                                case 'mobilePhone.prefix':
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: '', number: '' }
                                    }
                                    formikErrors.mobilePhone.prefix = msg || 'Invalid phone'
                                    break
                                case 'dateOfBirth':
                                    formikErrors.dateOfBirth = {
                                        day: msg || 'Invalid date',
                                        month: msg || 'Invalid date',
                                        year: msg || 'Invalid date',
                                    }
                                    break
                            }
                        }

                        console.log('[ApplyV2ProfileStep2] Setting formik errors:', formikErrors)
                        if (isMounted) {
                            // Використовуємо setTimeout, щоб встановити помилки після того, як форма завершить переініціалізацію
                            setTimeout(() => {
                                if (isMounted) {
                                    console.log(
                                        '[ApplyV2ProfileStep2] Setting errors after timeout (try):',
                                        formikErrors
                                    )
                                    formik.setErrors(formikErrors)

                                    const profileVisitCount = storageData?.profileVisitCount || 0
                                    const isFirstVisit = profileVisitCount <= 1

                                    if (!isFirstVisit) {
                                        const touchedFields: any = { ...formik.touched }
                                        for (const profileError of firstError.profileErrors) {
                                            const { field } = profileError
                                            if (
                                                field === 'mobilePhone' ||
                                                field === 'mobilePhone.number' ||
                                                field === 'mobilePhone.prefix'
                                            ) {
                                                touchedFields.mobilePhone = { prefix: true, number: true }
                                            } else if (field === 'dateOfBirth') {
                                                touchedFields.dateOfBirth = { day: true, month: true, year: true }
                                            } else {
                                                touchedFields[field] = true
                                            }
                                        }
                                        console.log(
                                            '[ApplyV2ProfileStep2] Setting touched fields (try):',
                                            touchedFields,
                                            'isFirstVisit:',
                                            isFirstVisit
                                        )
                                        formik.setTouched(touchedFields, false)
                                    }
                                    console.log(
                                        '[ApplyV2ProfileStep2] After setting errors (try) - formik.errors:',
                                        formik.errors
                                    )
                                }
                            }, 100)
                        }
                    } else {
                        console.log('[ApplyV2ProfileStep2] No profileErrors in firstError')
                    }
                } else {
                    console.log('[ApplyV2ProfileStep2] No errors in response or empty errors array')
                }
            } catch (error: any) {
                console.log('[ApplyV2ProfileStep2] Validation error caught:', error)
                console.log('[ApplyV2ProfileStep2] Error type:', typeof error)
                console.log('[ApplyV2ProfileStep2] Error keys:', Object.keys(error || {}))
                console.log('[ApplyV2ProfileStep2] Error.data:', error?.data)
                console.log('[ApplyV2ProfileStep2] Error.data type:', typeof error?.data)
                console.log('[ApplyV2ProfileStep2] Error.data keys:', error?.data ? Object.keys(error.data) : 'no data')
                console.log('[ApplyV2ProfileStep2] Error.data.errors:', error?.data?.errors)
                console.log('[ApplyV2ProfileStep2] Error.data.data:', error?.data?.data)

                let errors = null
                if (error?.data?.errors) {
                    errors = error.data.errors
                } else if (error?.data?.data?.errors) {
                    errors = error.data.data.errors
                } else if (error?.errors) {
                    errors = error.errors
                }

                if (errors) {
                    console.log('[ApplyV2ProfileStep2] Found errors:', errors)
                    console.log('[ApplyV2ProfileStep2] Errors type:', typeof errors)
                    console.log('[ApplyV2ProfileStep2] Errors is array:', Array.isArray(errors))

                    const firstError = Array.isArray(errors) ? errors[0] : errors
                    console.log('[ApplyV2ProfileStep2] First error from catch:', firstError)
                    console.log(
                        '[ApplyV2ProfileStep2] First error keys:',
                        firstError ? Object.keys(firstError) : 'no firstError'
                    )

                    if (firstError?.profileErrors && Array.isArray(firstError.profileErrors)) {
                        console.log('[ApplyV2ProfileStep2] Found profileErrors in catch:', firstError.profileErrors)
                        const formikErrors: any = {}

                        for (const profileError of firstError.profileErrors) {
                            const { field, msg } = profileError
                            console.log('[ApplyV2ProfileStep2] Processing profileError in catch:', { field, msg })

                            switch (field) {
                                case 'firstName':
                                case 'lastName':
                                case 'email':
                                case 'linkedin':
                                case 'gender':
                                case 'address':
                                case 'nationality':
                                case 'title':
                                    formikErrors[field] = msg || 'Required'
                                    break
                                case 'mobilePhone':
                                case 'mobilePhone.number':
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: '', number: '' }
                                    }
                                    formikErrors.mobilePhone.number = msg || 'Invalid phone'
                                    break
                                case 'mobilePhone.prefix':
                                    if (!formikErrors.mobilePhone) {
                                        formikErrors.mobilePhone = { prefix: '', number: '' }
                                    }
                                    formikErrors.mobilePhone.prefix = msg || 'Invalid phone'
                                    break
                                case 'dateOfBirth':
                                    formikErrors.dateOfBirth = {
                                        day: msg || 'Invalid date',
                                        month: msg || 'Invalid date',
                                        year: msg || 'Invalid date',
                                    }
                                    break
                            }
                        }

                        console.log('[ApplyV2ProfileStep2] Setting formik errors from catch:', formikErrors)
                        if (isMounted) {
                            // Використовуємо setTimeout, щоб встановити помилки після того, як форма завершить переініціалізацію
                            setTimeout(() => {
                                if (isMounted) {
                                    console.log(
                                        '[ApplyV2ProfileStep2] Setting errors after timeout (catch):',
                                        formikErrors
                                    )
                                    formik.setErrors(formikErrors)

                                    const profileVisitCount = storageData?.profileVisitCount || 0
                                    const isFirstVisit = profileVisitCount <= 1
                                    if (hasUserInteractedRef.current || !isFirstVisit) {
                                        const touchedFields: any = { ...formik.touched }
                                        for (const profileError of firstError.profileErrors) {
                                            const { field } = profileError
                                            if (
                                                field === 'mobilePhone' ||
                                                field === 'mobilePhone.number' ||
                                                field === 'mobilePhone.prefix'
                                            ) {
                                                touchedFields.mobilePhone = { prefix: true, number: true }
                                            } else if (field === 'dateOfBirth') {
                                                touchedFields.dateOfBirth = { day: true, month: true, year: true }
                                            } else {
                                                touchedFields[field] = true
                                            }
                                        }
                                        console.log(
                                            '[ApplyV2ProfileStep2] Setting touched fields (catch):',
                                            touchedFields,
                                            'isFirstVisit:',
                                            isFirstVisit,
                                            'profileVisitCount:',
                                            profileVisitCount
                                        )
                                        formik.setTouched(touchedFields, false)
                                    }
                                    console.log(
                                        '[ApplyV2ProfileStep2] After setting errors - formik.errors:',
                                        formik.errors
                                    )
                                }
                            }, 100)
                        }
                    } else {
                        console.log('[ApplyV2ProfileStep2] No profileErrors in catch firstError')
                        console.log('[ApplyV2ProfileStep2] firstError.profileErrors:', firstError?.profileErrors)
                        console.log(
                            '[ApplyV2ProfileStep2] firstError.profileErrors type:',
                            typeof firstError?.profileErrors
                        )
                        console.log(
                            '[ApplyV2ProfileStep2] firstError.profileErrors is array:',
                            Array.isArray(firstError?.profileErrors)
                        )
                    }
                } else {
                    console.log('[ApplyV2ProfileStep2] No errors found in catch')
                    console.log('[ApplyV2ProfileStep2] Full error object:', JSON.stringify(error, null, 2))
                }
            }
        }

        validateProfile()

        return () => {
            isMounted = false
        }
        // eslint-disable-next-line
    }, [isFormInitialized, postProfileData?.data?.uid])

    const debouncedCheckEmail = useCallback(
        debounce(async (email: string) => {
            if (email && email.length > 3) {
                try {
                    addLoadingField('email')
                    const result = await checkEmailDuplicate({ email: email.trim() }).unwrap()
                    if (result.data?.isUnique === false) {
                        setIsEmailDuplicate(true)
                        formik.setFieldError(
                            'email',
                            getTitle(
                                '10184',
                                'This email is already registered. Log in with it or enter a different one.'
                            )
                        )
                    }
                } catch (error: any) {
                    console.log(error?.data?.errors)
                    const errorMessage = (error?.data?.errors ?? [])
                        .map((err: { msg: string; code: number }) => err.msg)
                        .join(', ')
                    formik.setFieldError(
                        'email',
                        errorMessage || getTitle('10529', 'An error occurred while checking the email')
                    )
                } finally {
                    removeLoadingField('email')
                }
            }
        }, 800),
        [checkEmailDuplicate, formik]
    )

    const debouncedCheckPhone = useCallback(
        debounce(async (phone: { prefix: string | null | undefined; number: string | null | undefined }) => {
            if (phone?.prefix && phone?.number && phone.number.length > 4) {
                addLoadingField('mobilePhone')
                try {
                    const result = await checkPhoneDuplicate({
                        mobilePhone: {
                            prefix: phone.prefix.trim(),
                            number: phone.number.trim(),
                        },
                    }).unwrap()
                    if (result.data?.isUnique === false) {
                        formik.setFieldError(
                            'mobilePhone.number',
                            getTitle(
                                '10185',
                                'This phone number is already registered. Log in with the registered email or enter a different number.'
                            )
                        )
                    }
                } catch (error: any) {
                    formik.setFieldError(
                        'mobilePhone.number',
                        getTitle('10530', 'An error occurred while checking the phone')
                    )
                    formik.setFieldError(
                        'mobilePhone.prefix',
                        getTitle('10530', 'An error occurred while checking the phone')
                    )
                } finally {
                    removeLoadingField('mobilePhone')
                }
            }
        }, 800),
        [checkPhoneDuplicate, formik]
    )

    // const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     setIsEmailDuplicate(false)
    //
    //     formik.setFieldValue('email', e.target.value).then((errs) => {
    //         formik.setFieldTouched('email', true).then((unfil) => {
    //             console.log(formik.touched, errs, unfil)
    //
    //             if (debounceTimers.current['email']) {
    //                 clearTimeout(debounceTimers.current['email']!)
    //             }
    //             console.log(errs?.email, unfil?.email)
    //
    //             debounceTimers.current['email'] = setTimeout(() => {
    //                 if (errs?.email || unfil?.email) {
    //                     return
    //                 }
    //                 debouncedCheckEmail(e.target.value)
    //             }, 800)
    //             // }
    //         })
    //     })
    // }

    // const handlePhoneChange = (phoneValue: {
    //     prefix: string | undefined | null
    //     number: string | undefined | null
    // }) => {
    //     setIsPhoneDuplicate(false)
    //     formik.setFieldValue('mobilePhone', phoneValue).then((errs) => {
    //         if (debounceTimers.current['mobilePhone']) {
    //             clearTimeout(debounceTimers.current['mobilePhone']!)
    //         }
    //         debounceTimers.current['mobilePhone'] = setTimeout(() => {
    //             debouncedCheckPhone(phoneValue)
    //         }, 700)
    //     })
    // }

    const handleFieldChange = <K extends keyof FormikValues>(field: K, value: FormikValues[K]) => {
        if (!postProfileData?.data?.uid) return

        let finalValue: any = null

        switch (field) {
            case 'dateOfBirth':
                const dobValue = value as FormikValues['dateOfBirth']
                const dobString =
                    dobValue?.year && dobValue?.month && dobValue?.day
                        ? `${dobValue.year}-${dobValue.month}-${dobValue.day}`
                        : null
                finalValue = dobString !== '0000-00-00' ? dobString : null
                break

            case 'address':
                const addressValue = value as FormikValues['address']
                finalValue =
                    addressValue?.country ||
                    addressValue?.city ||
                    addressValue?.postalCode ||
                    addressValue?.address1 ||
                    addressValue?.address2
                        ? {
                              country: addressValue?.country ?? '',
                              city: addressValue?.city ?? '',
                              postalCode: addressValue?.postalCode ?? '',
                              address1: addressValue?.address1 ?? '',
                              address2: addressValue?.address2 ?? '',
                          }
                        : null
                break

            case 'mobilePhone':
                const phoneValue = value as FormikValues['mobilePhone']
                finalValue =
                    phoneValue?.prefix && phoneValue?.number
                        ? {
                              prefix: phoneValue.prefix ?? '',
                              number: phoneValue.number ?? '',
                          }
                        : null
                break

            case 'gender':
            case 'nationality':
                finalValue = value ?? null
                break

            case 'firstName':
            case 'lastName':
            case 'email':
            case 'title':
            case 'linkedin':
                finalValue = value ? String(value).trim() : null
                break

            default:
                finalValue = value
        }

        const body = { [field]: finalValue } as PutProfileFieldDataV2Body

        formik.setFieldValue(field, value).then((errs) => {
            addLoadingField(field)

            if (field === 'email' && isEmailDuplicate) {
                setIsEmailDuplicate(false)
            }
            if (field === 'mobilePhone' && isPhoneDuplicate) {
                setIsPhoneDuplicate(false)
            }
            if (field === 'firstName' || field === 'lastName' || field === 'email') {
                formik.setFieldTouched(field, true)
            }
            if (debounceTimers.current[field]) {
                clearTimeout(debounceTimers.current[field])
            }
            debounceTimers.current[field] = setTimeout(
                () => {
                    putProfileField({
                        uid: postProfileData?.data?.uid,
                        body: body,
                    })
                        .unwrap()
                        .then((res) => {
                            // console.log(res)
                            // if (field === 'email' && finalValue?.length > 3) {
                            //     checkEmailDuplicate({ email: finalValue.trim() })
                            //         .unwrap()
                            //         .then((result) => {
                            //             if (result.data?.isUnique === false) {
                            //                 setIsEmailDuplicate(true)
                            //                 formik.setFieldError(
                            //                     'email',
                            //                     getTitle(
                            //                         '10184',
                            //                         'This email is already registered. Log in with it or enter a different one.'
                            //                     )
                            //                 )
                            //             }
                            //         })
                            // }
                        })
                        .catch((error: any) => {
                            console.log(error)
                            if (error?.data?.errors && Array.isArray(error.data.errors)) {
                                const formikErrors: any = { ...formik.errors }

                                error.data.errors.forEach((err: { msg: string; field: string; code: number }) => {
                                    switch (err.field) {
                                        // case 'mobilePhone':
                                        //     if (!formikErrors.mobilePhone) {
                                        //         formikErrors.mobilePhone = { prefix: '', number: '' }
                                        //     }
                                        //     formikErrors.mobilePhone.prefix = err.msg
                                        //     formikErrors.mobilePhone.number = err.msg
                                        //     break
                                        case 'mobilePhone.prefix':
                                            if (!formikErrors.mobilePhone) {
                                                formikErrors.mobilePhone = { prefix: '', number: '' }
                                            }
                                            formikErrors.mobilePhone.prefix = err.msg
                                            break
                                        case 'mobilePhone.number':
                                            if (!formikErrors.mobilePhone) {
                                                formikErrors.mobilePhone = { prefix: '', number: '' }
                                            }
                                            formikErrors.mobilePhone.number = err.msg
                                            break
                                        case 'dateOfBirth':
                                            if (!formikErrors.dateOfBirth) {
                                                formikErrors.dateOfBirth = { day: '', month: '', year: '' }
                                            }
                                            formikErrors.dateOfBirth.day = err.msg
                                            formikErrors.dateOfBirth.month = err.msg
                                            formikErrors.dateOfBirth.year = err.msg
                                            // formik.setTouched(
                                            //     {
                                            //         ...formik.touched,
                                            //         dateOfBirth: {
                                            //             year: true,
                                            //             month: true,
                                            //             day: true,
                                            //         },
                                            //     },
                                            //     true
                                            // )
                                            Promise.all([
                                                formik.setFieldTouched('dateOfBirth.day', true),
                                                formik.setFieldTouched('dateOfBirth.month', true),
                                                formik.setFieldTouched('dateOfBirth.year', true),
                                            ])
                                            break
                                        case 'firstName':
                                        case 'lastName':
                                        case 'email':
                                            if (err.code === 1205) {
                                                setIsEmailDuplicate(true)
                                            }
                                            formikErrors[err.field] = err.msg
                                            break
                                        case 'mobilePhone':
                                            if (err.code === 1206) {
                                                setIsPhoneDuplicate(true)
                                            }
                                            if (!formikErrors.mobilePhone) {
                                                formikErrors.mobilePhone = { prefix: err.msg, number: err.msg }
                                            } else {
                                                formikErrors.mobilePhone.prefix = err.msg
                                                formikErrors.mobilePhone.number = err.msg
                                            }
                                            // formikErrors[err.field] = err.msg
                                            break
                                        case 'linkedin':
                                        case 'gender':
                                        case 'address':
                                        case 'nationality':
                                        case 'title':
                                            formikErrors[err.field] = err.msg
                                            break
                                        default:
                                            formikErrors[err.field] = err.msg
                                            break
                                    }
                                })

                                formik.setErrors(formikErrors)
                                console.log(formik.errors)
                            } else {
                                formik.setErrors({
                                    root: error.message,
                                } as any)
                            }
                        })
                        .finally(() => {
                            removeLoadingField(field)
                        })
                },
                700,
                formik
            )
        })
    }

    const handleSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault()

            formik.validateForm().then((errors) => {
                if (Object.keys(errors).length > 0) {
                    const touchedFields: any = {}
                    Object.keys(errors).forEach((field) => {
                        if (field === 'mobilePhone') {
                            touchedFields.mobilePhone = { prefix: true, number: true }
                        } else if (field === 'dateOfBirth') {
                            touchedFields.dateOfBirth = { day: true, month: true, year: true }
                        } else {
                            touchedFields[field] = true
                        }
                    })

                    formik.setTouched(touchedFields)

                    setTimeout(() => {
                        scrollToFirstError()
                    }, 100)
                } else {
                    formik.handleSubmit(e)
                }
            })
        },
        [formik, scrollToFirstError]
    )

    return (
        <form
            ref={formRef}
            className={baseClass}
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    e.preventDefault()

                    const form = e.currentTarget
                    const inputs = Array.from(form.querySelectorAll('input, select, textarea')).filter(
                        (el) =>
                            !el.hasAttribute('disabled') &&
                            !el.hasAttribute('readonly') &&
                            el.getAttribute('type') !== 'hidden'
                    )

                    const index = inputs.indexOf(e.target as HTMLElement)
                    const nextInput = inputs[index + 1] as HTMLElement | undefined

                    if (nextInput) {
                        nextInput.focus()
                    } else {
                        formik.handleSubmit(e)
                    }
                }
            }}
            noValidate={false}
        >
            {orderedList.map((item) => {
                switch (item.name) {
                    case 'firstName':
                        return (
                            <div className={`${baseClass}__row`} key={item.name}>
                                {isFirstNameVisible && (
                                    <ApplyV2InputText
                                        name="firstName"
                                        // name={item.name}
                                        className={`${baseClass}__input`}
                                        label={getTitle('169', 'First name')}
                                        value={formik.values.firstName}
                                        onChange={(e) => {
                                            hasUserInteractedRef.current = true
                                            // formik.setFieldTouched('firstName', true)
                                            // formik.handleChange(e)
                                            handleFieldChange('firstName', e.target.value)
                                        }}
                                        onBlur={(e) => {
                                            hasUserInteractedRef.current = true
                                            formik.handleBlur(e)
                                        }}
                                        placeholder={getTitle('169', 'Enter your first name')}
                                        errorMessage={formik.touched.firstName && formik.errors.firstName}
                                        isRequired={isFirstNameRequired}
                                        // isLoading={isLoadingField('firstName')}
                                        isDisabled={!!candidateId && !!formik.values.firstName}
                                    />
                                )}
                                {isLastNameRequired && (
                                    <ApplyV2InputText
                                        name="lastName"
                                        className={`${baseClass}__input`}
                                        label={getTitle('170', 'Last name')}
                                        value={formik.values.lastName}
                                        onChange={(e) => {
                                            hasUserInteractedRef.current = true
                                            // formik.setFieldTouched('lastName', true)
                                            // formik.handleChange(e)
                                            handleFieldChange('lastName', e.target.value)
                                        }}
                                        onBlur={(e) => {
                                            hasUserInteractedRef.current = true
                                            formik.handleBlur(e)
                                        }}
                                        placeholder={getTitle('170', 'Enter your last name')}
                                        errorMessage={formik.touched.lastName && formik.errors.lastName}
                                        isRequired={isLastNameRequired}
                                        // isLoading={isLoadingField('lastName')}
                                        isDisabled={!!candidateId && !!formik.values.lastName}
                                    />
                                )}
                            </div>
                        )
                    case 'lastName':
                        return null
                    case 'email':
                        return (
                            <div className={`${baseClass}__contacts`} key={item.name}>
                                <ApplyV2InputText
                                    name="email"
                                    label={getTitle('92', 'Email')}
                                    value={formik.values.email}
                                    onChange={(e) => {
                                        hasUserInteractedRef.current = true
                                        handleFieldChange('email', e.target.value)
                                    }}
                                    // onChange={handleEmailChange}
                                    onBlur={(e) => {
                                        hasUserInteractedRef.current = true
                                        formik.handleBlur(e)
                                    }}
                                    placeholder={getTitle('92', 'Enter your email')}
                                    errorMessage={formik.touched.email && formik.errors.email}
                                    isShowErrorMessage={!isEmailDuplicate}
                                    isRequired={isEmailRequired}
                                    // isLoading={isCheckEmailDuplicateLoading || isLoadingField('email')}
                                    isDisabled={!!candidateId && !!formik.values.email}
                                />
                                {isEmailDuplicate && (
                                    <ErrorMessageDuplicate type={'email'} url={formik.values.email ?? ''} />
                                )}
                            </div>
                        )
                    case 'mobilePhone':
                        return (
                            <div className={`${baseClass}__contacts`} key={item.name}>
                                <ApplyV2InputPhone
                                    name="mobilePhone"
                                    label={getTitle('137', 'Mobile phone')}
                                    value={formik.values.mobilePhone}
                                    onChange={(phoneValue) => {
                                        // formik.setFieldTouched('mobilePhone', true)
                                        // formik.setFieldValue('mobilePhone', phoneValue)
                                        handleFieldChange('mobilePhone', phoneValue)
                                    }}
                                    // onChange={handlePhoneChange}
                                    onBlur={formik.handleBlur}
                                    errorMessage={formik.touched.mobilePhone && formik.errors.mobilePhone}
                                    isShowErrorMessage={!isPhoneDuplicate}
                                    isRequired={isMobilePhoneRequired}
                                    // isLoading={isCheckPhoneDuplicateLoading || isLoadingField('mobilePhone')}
                                />
                                {isPhoneDuplicate && (
                                    <ErrorMessageDuplicate
                                        type={'phone'}
                                        url={
                                            formik.values.mobilePhone?.number && formik.values.mobilePhone?.prefix
                                                ? `${formik.values.mobilePhone?.prefix}${formik.values.mobilePhone?.number}`
                                                : ''
                                        }
                                    />
                                )}
                            </div>
                        )
                    case 'linkedin':
                        return (
                            <ApplyV2InputText
                                key={item.name}
                                name="linkedin"
                                label={getTitle('9691', 'LinkedIn profile link')}
                                value={formik.values.linkedin}
                                onChange={(e) => {
                                    // formik.setFieldTouched('linkedin', true)
                                    // formik.handleChange(e)
                                    handleFieldChange(item.name, e.target.value)
                                }}
                                onBlur={formik.handleBlur}
                                placeholder="https://www.linkedin.com/in/username"
                                errorMessage={formik.touched.linkedin && formik.errors.linkedin}
                                isRequired={isLinkedInRequired}
                                // isLoading={isLoadingField('linkedin')}
                            />
                        )
                    case 'gender':
                        return (
                            <ApplyV2SelectSingle
                                key={item.name}
                                name="gender"
                                label={getTitle('66', 'Gender')}
                                value={formik.values.gender}
                                onChange={(e) => {
                                    // formik.setFieldValue('gender', e).then(() => {
                                    //     // formik.setFieldTouched('gender', true)
                                    // })
                                    handleFieldChange(item.name, e as GenderType)
                                }}
                                options={[
                                    { value: 1, label: getTitle('68', 'Male') },
                                    { value: 2, label: getTitle('69', 'Female') },
                                    { value: 3, label: getTitle('1144', 'Other') },
                                ]}
                                onBlur={formik.handleBlur}
                                placeholder={getTitle('8887', 'Select your gender')}
                                // isSearchable
                                errorMessage={formik.touched.gender && formik.errors.gender}
                                isRequired={isGenderRequired}
                                // isLoading={isLoadingField('gender')}
                            />
                        )
                    case 'nationality':
                        return (
                            <>
                                <ApplyV2SelectSingle
                                    key={item.name}
                                    name="nationality"
                                    label={getTitle('132', 'Nationality')}
                                    value={formik.values.nationality}
                                    options={nationalityOptions.map(({ id, name }) => ({
                                        value: id,
                                        label: name,
                                    }))}
                                    onChange={(e) => {
                                        console.log(e)
                                        handleFieldChange(item.name, e)
                                    }}
                                    onBlur={formik.handleBlur}
                                    placeholder={getTitle('132', 'Select nationality')}
                                    errorMessage={formik.touched.nationality && formik.errors.nationality}
                                    isRequired={isNationalityRequired}
                                />
                                {/*<ApplyV2Select*/}
                                {/*    key={item.name}*/}
                                {/*    name="nationality"*/}
                                {/*    label={getTitle('132', 'Nationality')}*/}
                                {/*    value={formik.values.nationality}*/}
                                {/*    onChange={(e) => {*/}
                                {/*        // formik.setFieldValue('nationality', e).then(() => {*/}
                                {/*        //     // formik.setFieldTouched('nationality', true)*/}
                                {/*        // })*/}
                                {/*        handleFieldChange(item.name, e)*/}
                                {/*    }}*/}
                                {/*    options={nationalityOptions.map(({ id, name }) => ({*/}
                                {/*        value: id,*/}
                                {/*        label: name,*/}
                                {/*    }))}*/}
                                {/*    onBlur={formik.handleBlur}*/}
                                {/*    placeholder={getTitle('132', 'Select nationality')}*/}
                                {/*    isSearchable*/}
                                {/*    errorMessage={formik.touched.nationality && formik.errors.nationality}*/}
                                {/*    isRequired={isNationalityRequired}*/}
                                {/*    // isLoading={isLoadingField('nationality')}*/}
                                {/*/>*/}
                            </>
                        )
                    case 'title':
                        return (
                            <ApplyV2InputText
                                key={item.name}
                                name="title"
                                label={getTitle('171', 'Title')}
                                value={formik.values.title}
                                onChange={(e) => {
                                    // formik.setFieldTouched('title', true)
                                    // formik.handleChange(e)
                                    handleFieldChange(item.name, e.target.value)
                                }}
                                onBlur={formik.handleBlur}
                                placeholder={getTitle('9000', 'Enter your title')}
                                errorMessage={formik.touched.title && formik.errors.title}
                                isRequired={isTitleRequired}
                                // isLoading={isLoadingField('title')}
                            />
                        )
                    case 'dateOfBirth':
                        return (
                            <ApplyV2DateSelectGroup
                                key={item.name}
                                label={getTitle('131', 'Date of birth')}
                                names={{
                                    day: 'dateOfBirth.day',
                                    month: 'dateOfBirth.month',
                                    year: 'dateOfBirth.year',
                                }}
                                value={{
                                    day: formik.values.dateOfBirth?.day,
                                    month: formik.values.dateOfBirth?.month,
                                    year: formik.values.dateOfBirth?.year,
                                }}
                                onChange={({ day, month, year }) => {
                                    handleFieldChange(item.name, { day, month, year })
                                    // formik.setValues({
                                    //     ...formik.values,
                                    //     dateOfBirth: { day, month, year },
                                    // })
                                }}
                                errors={{
                                    day:
                                        (formik.touched.dateOfBirth as any)?.day &&
                                        (formik.errors.dateOfBirth as any)?.day,
                                    month:
                                        (formik.touched.dateOfBirth as any)?.month &&
                                        (formik.errors.dateOfBirth as any)?.month,
                                    year:
                                        (formik.touched.dateOfBirth as any)?.year &&
                                        (formik.errors.dateOfBirth as any)?.year,
                                }}
                                onBlur={formik.handleBlur}
                                isFutureYears={false}
                                yearsRange={90}
                                isCurrentYear={true}
                                isRequired={isDateOfBirthRequired}
                                // isClearable={true}
                            />
                        )
                    case 'address':
                        return (
                            <ApplyV2AddressSelect
                                key={item.name}
                                name={'address'}
                                label={getTitle('358', 'Address')}
                                value={formik.values.address}
                                onChange={(addr) => {
                                    // formik.setFieldValue('address', addr).then(() => {
                                    //     // formik.setFieldTouched('address', true)
                                    // })
                                    handleFieldChange(item.name, addr)
                                }}
                                onBlur={formik.handleBlur}
                                placeholder={getTitle('1979', 'Enter address')}
                                isSearchable
                                errorMessage={formik.touched.address && formik.errors.address}
                                isRequired={isAddressRequired}
                                // isLoading={isLoadingField('address')}
                                isClearable={true}
                            />
                        )
                    default:
                        return null
                }
            })}

            <div className={`ApplyDialogV2__next ${baseClass}__next`}>
                {!settingsData?.steps?.profile?.required && (
                    <ApplyV2Button
                        variant={'transparent'}
                        className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-skip`}
                        rightIcon={'>'}
                        // isDisabled={!isValid || isLoading}
                        onClick={() => {
                            onNextStep()
                        }}
                    >
                        {getTitle('6107', 'Skip')}
                    </ApplyV2Button>
                )}
                {/*<ApplyV2Button*/}
                {/*    type={'button'}*/}
                {/*    onClick={() => {*/}
                {/*        console.log(formik.touched, formik.errors, formik.values)*/}
                {/*    }}*/}
                {/*>*/}
                {/*    YYTESSS*/}
                {/*</ApplyV2Button>*/}
                <ApplyV2Button
                    type="submit"
                    variant={'primary'}
                    className={`ApplyDialogV2__next--btn ApplyDialogV2__next--btn-submit`}
                    isLoading={formik.isSubmitting}
                    isDisabled={formik.isSubmitting || isPhoneDuplicate || isEmailDuplicate}
                    onClick={() => {
                        console.log('TEST form errors: -', formik.errors)
                        console.log('TEST form values: -', formik.values)
                    }}
                >
                    {getTitle('3035', 'Next')}
                </ApplyV2Button>
            </div>
        </form>
    )
}

interface ErrorMessageDuplicateProps {
    type: 'email' | 'phone'
    url: string
}

function ErrorMessageDuplicate({ type = 'email', url = '' }: ErrorMessageDuplicateProps) {
    const { getTitle } = useApplyV2NavTranslations()
    const navigate = useNavigate()

    let errorMessage = ''

    switch (type) {
        case 'email':
            errorMessage = getTitle(
                '10184',
                'This email is already registered. Log in with it or enter a different one.'
            )
            break
        case 'phone':
            errorMessage = getTitle(
                '10185',
                'This phone number is already registered. Log in with the registered email or enter a different number.'
            )
            break
        default:
    }

    return (
        <div className={'ErrorMessageDuplicate'}>
            <svg
                className="ErrorMessageDuplicate__icon"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="#e53e3e"
                viewBox="0 0 24 24"
            >
                <circle cx="12" cy="12" r="10" fill="#e53e3e" />
                <path
                    d="M12 8v4M12 16h.01"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </svg>
            <span className="ErrorMessageDuplicate__message">{errorMessage}</span>
            <button
                className="ErrorMessageDuplicate__btn"
                type="button"
                // onClick={() => navigate(`/login?${type}=${url.trim()}`)}
                onClick={() => {
                    window.location.href = `/login?${type}=${url.trim()}`
                }}
            >
                {getTitle('1890', 'Log in')}
            </button>
        </div>
    )
}

export default ApplyV2ProfileStep2
